/* eslint-disable react/no-unsafe */
/*
 * Mounts a video player and adds Khan academy related addons to it such as a
 * custom play button and a points display.
 *
 * NOTE: If you're thinking of adding COOL INTERACTIVE QUESTIONS to the player,
 *       there's a bunch of code that you can leverage! The legacy player had a
 *       tool called Socrates, which was removed in the same commit that added
 *       this comment.
 *
 * WARNING(kevinb): `playerModel` is typed as `any`.
 */
import {plural} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import {color} from "@khanacademy/wonder-blocks-tokens";
import {PermissionsInfo} from "@repo/shared-core";
import {mediaQueries, standardTransition} from "@repo/shared-styles";

import * as sharedStyles from "#content-library-legacy/styles/shared.ts";
import type {EndOfVideoAttributionType} from "#content-types-package/practice-types.ts";
import withPrefersReducedMotion, {
    type WithoutPrefersReducedMotion,
    type WithPrefersReducedMotionProps,
} from "#data-access/with-prefers-reduced-motion.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";
import APIActionResults from "#shared-package/api-action-results.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import {deprecatedTypography} from "#shared-styles-package/deprecated-typography.ts";
import type {
    TaskType,
    TutorialNavContent,
} from "#tutorial-types-package/types.ts";

import {VideoPlayerState} from "./constants.ts";
import EndOfVideoCard from "./end-of-video-card.tsx";
import {heightOfVideoplayerControls} from "./shared-styles.ts";
import type {VideoPlayerModelType, VideoPlayerStateType} from "./types.ts";
import VideoPlayer from "./video-player/video-player.tsx";

type OwnProps = {
    // Don't show large play button or points overlay.
    // Useful when the video is really small.
    hideOverlays: boolean;
    // Used to report page usable metrics
    onComponentInteractive: () => void;
    // Sometimes when the video is in a modal we don't want the video resizing.
    // This allows us to specify a fixed size.
    size?: {
        height: number;
        width: number;
    };
    // TODO(kevinb): migrate to object-fit:cover with resize event listener
    inModal: boolean;
    // Whether the player is in an /embed_video frame.
    embedded?: boolean;
    navigateToNextItem?: () => unknown;
    nextContentItem: TutorialNavContent | null | undefined;
    // Called whenever the state of the player changes.
    onStateChanged?: (state: VideoPlayerStateType) => void;
    playerModel: VideoPlayerModelType;
    // In certain situations we'd like to override the default autoplay
    // behavior, e.g. in class-package/content/preview-modal.jsx.
    shouldAutoplay?: boolean;
    task: TaskType | null | undefined;
    video: Readonly<{
        id?: string;
        // below matches stateful-video-wrapper.jsx's video prop
        kaUrl: string;
        translatedTitle: string;
        translatedYoutubeId: string;
        youtubeId: string;
        slug: string;
    }>;
    attribution: EndOfVideoAttributionType | null | undefined;
};

type Props = OwnProps & WithUserProfileProps & WithPrefersReducedMotionProps;

type DefaultProps = {
    hideOverlays: Props["hideOverlays"];
    onComponentInteractive: Props["onComponentInteractive"];
    inModal: Props["inModal"];
};

type State = {
    currentTask: TaskType | null | undefined;
    isFocusingPlayer: boolean;
    isHoveringPlayer: boolean;
    playerState: (typeof VideoPlayerState)[keyof typeof VideoPlayerState];
    pointsEarned: number;
};

class KAVideoWrapper extends React.Component<Props, State> {
    pointsInterval: number | null | undefined;
    // @ts-expect-error - TS2564 - Property '_isMounted' has no initializer and is not definitely assigned in the constructor.
    _isMounted: boolean;
    // @ts-expect-error - TS2564 - Property 'unsubscribes' has no initializer and is not definitely assigned in the constructor.
    unsubscribes: Array<() => void>;

    static defaultProps: DefaultProps = {
        hideOverlays: false,
        onComponentInteractive: () => {},
        inModal: false,
    };

    state: State = {
        // NOTE(diedra): currentTask always starts as null in modals.
        currentTask: this.props && this.props.task,
        isFocusingPlayer: false,
        isHoveringPlayer: false,

        // TODO(riley): We don't need to track this in so many places.
        playerState: VideoPlayerState.UNSTARTED,

        pointsEarned: 0,
    };

    componentDidMount() {
        this._isMounted = true;

        this.unsubscribes = [
            // Ensure that we've got the most recent task data.
            APIActionResults.register(
                "current_task",
                this.updateTaskIfNecessary,
            ),
        ];

        // TODO(jeff, LP-6954): Use Wonder Blocks Timing API
        // @ts-expect-error - TS2322 - Type 'Timer' is not assignable to type 'number'.
        // eslint-disable-next-line no-restricted-syntax
        this.pointsInterval = setInterval(this.updatePointsEarned, 5000);
    }

    UNSAFE_componentWillUpdate(nextProps: Props) {
        // Reset the video
        if (
            nextProps.video.translatedYoutubeId !==
            this.props.video.translatedYoutubeId
        ) {
            this.setState({
                playerState: VideoPlayerState.UNSTARTED,
                pointsEarned: 0,
            });
        }
    }

    componentWillUnmount() {
        if (this.pointsInterval) {
            // TODO(jeff, LP-6954): Use Wonder Blocks Timing API
            // eslint-disable-next-line no-restricted-syntax
            clearInterval(this.pointsInterval);
        }
        this.unsubscribes.forEach((unsubscribe) => unsubscribe());
    }

    updateTaskIfNecessary: (task: TaskType) => void = (task) => {
        const {video} = this.props;
        const {currentTask} = this.state;

        if (
            // Video modals will always have null currentTask's. Let it through.
            currentTask !== null &&
            (!task.content || currentTask === undefined || !currentTask.content)
        ) {
            return;
        }

        const currentId =
            currentTask && currentTask.content && currentTask.content.id
                ? currentTask.content.id
                : null;
        const newId =
            task && task.content && task.content.id ? task.content.id : null;
        const isNewId = currentTask == null || newId !== currentId;

        // An API action provided information about the current task. Make sure
        // it's for the current video though (and that we haven't already
        // noticed it), rather than for an old XHR that finished late.
        if (isNewId && newId === video.id && this._isMounted) {
            this.setState({currentTask: task});
        }
    };

    getCurrentTask(): TaskType | null | undefined {
        const {currentTask} = this.state;
        if (
            currentTask &&
            currentTask.content &&
            currentTask.content.id === this.props.video.id
        ) {
            return currentTask;
        }
        // We don't have a task for this video yet. It's unusual for the
        // user to finish watching a video before we load their UserVideo
        // and task for it, so, oh well; we just won't include a point
        // bounty.
        return null;
    }

    updatePointsEarned: () => void = () => {
        this.setState({
            pointsEarned: this.props.playerModel.get("points") || 0,
        });
    };

    handleNextButtonClick: () => void = () => {
        const {navigateToNextItem, nextContentItem, playerModel} = this.props;

        if (nextContentItem && navigateToNextItem) {
            navigateToNextItem();
        } else if (playerModel.player) {
            playerModel.player.seekTo(0);
            // NOTE: Unlike the YouTube player, the (fallback) HTML player does
            // not automatically start playing when changing the video current
            // time (which is what seekTo does) - our desired behavior.
            // As well, we cannot move the play call into seekTo, as that will
            // break existing behaviour of jumping to a specific time in the
            // Transcript tab - we don't want it to auto play in that case.
            playerModel.player.playVideo();
        }
    };

    handleViewAllClick: () => void = () => {
        // NOTE(jordan): Delete this once we can tap into the global nav to
        // open dialogs in a safer way.
        const dropdown = document.querySelector(
            "[data-notifications-dropdown-trigger]",
        );

        if (dropdown) {
            // @ts-expect-error - TS2339 - Property 'click' does not exist on type 'Element'.
            dropdown.click();
        }
    };

    handleStateChange: (e: {data: VideoPlayerStateType}) => void = (e) => {
        const {onStateChanged} = this.props;
        const playerState = e.data;

        // Do a final point update
        this.updatePointsEarned();

        this.setState({playerState}, () => {
            onStateChanged && onStateChanged(playerState);
        });
    };

    handlePlayerFocus: () => void = () => {
        if (!this._isMounted) {
            return;
        }

        this.setState({
            isFocusingPlayer: true,
        });
    };

    handlePlayerBlur: () => void = () => {
        this.setState({
            isFocusingPlayer: false,
        });
    };

    handlePlayerMouseEnter: () => void = () => {
        this.setState({
            isHoveringPlayer: true,
        });
    };

    handlePlayerMouseLeave: () => void = () => {
        this.setState({
            isHoveringPlayer: false,
        });
    };

    render(): React.ReactNode {
        const {
            attribution,
            embedded,
            hideOverlays,
            nextContentItem,
            onComponentInteractive,
            playerModel,
            size,
            video,
            inModal,
            shouldAutoplay,
        } = this.props;
        const {isFocusingPlayer, isHoveringPlayer, playerState, pointsEarned} =
            this.state;

        const isPlayerPlaying = playerState === VideoPlayerState.PLAYING;
        const isPlayerEnded = playerState === VideoPlayerState.ENDED;
        const isPlayerPaused = playerState === VideoPlayerState.PAUSED;

        // If the video is over, include bonus points for completing the
        // current video task.
        let totalPointsEarned = pointsEarned;
        const currentTask = this.getCurrentTask();
        if (isPlayerEnded && currentTask) {
            totalPointsEarned += currentTask.pointBounty;
        }

        const showEnergyPoints =
            (isPlayerPlaying || isPlayerPaused) &&
            (isHoveringPlayer || isFocusingPlayer) &&
            !hideOverlays;

        return (
            <div className={css(styles.videoBackdrop)}>
                <div className={css(styles.videoContainer)}>
                    <VideoPlayer
                        hidePlayButton={hideOverlays}
                        embedded={embedded}
                        iframeId={"video_" + video.youtubeId}
                        inModal={inModal}
                        // THIS KEY IS IMPORTANT! `VideoPlayer` has a bunch of
                        // side-effects on the model that trigger on mount.
                        // TODO(riley): Please, for the love of Sal, fix this.
                        key={video.youtubeId}
                        model={playerModel}
                        onComponentInteractive={onComponentInteractive}
                        onPlayerMouseEnter={this.handlePlayerMouseEnter}
                        onPlayerMouseLeave={this.handlePlayerMouseLeave}
                        onStateChange={this.handleStateChange}
                        playerState={playerState}
                        size={size}
                        title={video.translatedTitle}
                        shouldAutoplay={shouldAutoplay}
                    />

                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO: Address a11y error */}
                    <div
                        aria-hidden={!showEnergyPoints}
                        className={css(
                            styles.pointsContainer,
                            showEnergyPoints && styles.pointsContainerVisible,
                        )}
                        onFocus={this.handlePlayerFocus}
                        onBlur={this.handlePlayerBlur}
                        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- TODO: Address a11y error
                        tabIndex={0}
                    >
                        <div className={css(styles.points)}>
                            {plural(totalPointsEarned, {
                                one: "# energy point",
                                other: "# energy points",
                            })}
                        </div>
                    </div>

                    {isPlayerEnded && currentTask !== null && (
                        <div className={css(styles.endOfVideoCardContainer)}>
                            <EndOfVideoCard
                                pointsEarned={totalPointsEarned}
                                nextContentItem={nextContentItem}
                                onButtonClick={this.handleNextButtonClick}
                                onViewAllClick={this.handleViewAllClick}
                                isLoggedIn={PermissionsInfo.isLoggedIn}
                                url={video.kaUrl}
                                attribution={attribution}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

const extendToVideoPlayerControls = sharedStyles.mixins.position(
    "absolute",
    0,
    0,
    heightOfVideoplayerControls,
    0,
);

const styles = StyleSheet.create({
    videoBackdrop: {
        background: "#000",
        boxSizing: "border-box",
        display: "flex",
        // Needed to fix a bug on iOS where it doesn't honor the min content
        // size of flex items
        flexShrink: 0,
        width: "100%",
        justifyContent: "center",
    },

    videoContainer: {
        position: "relative",
        width: "100%",
    },

    pointsContainer: {
        alignItems: "flex-start",
        display: "flex",
        justifyContent: "center",
        opacity: 0,
        pointerEvents: "none",
        transition: `opacity ${standardTransition}`,

        [mediaQueries.smOrSmaller]: {
            display: "none",
        },

        ...extendToVideoPlayerControls,
    },

    pointsContainerVisible: {
        opacity: 1,
    },

    endOfVideoCardContainer: {
        ...extendToVideoPlayerControls,
    },

    points: {
        ...deprecatedTypography.bodySmall,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        border: "1px solid rgba(255, 255, 255, 0.5)",
        borderBottomLeftRadius: 4,
        borderBottomRightRadius: 4,
        borderTop: "none",
        color: color.white,
        padding: "16px 32px",
    },
});

type ExportProps = WithoutPrefersReducedMotion<
    WithoutUserProfile<
        JSX.LibraryManagedAttributes<
            typeof KAVideoWrapper,
            React.ComponentProps<typeof KAVideoWrapper>
        >
    >
>;

export default compose(
    withUserProfile,
    withPrefersReducedMotion,
)(KAVideoWrapper) as React.ComponentType<ExportProps>;
